import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import InvoiceFilters from './components/InvoiceFilters';
import InvoiceTable from './components/InvoiceTable';
import InvoiceMobileCard from './components/InvoiceMobileCard';
import BulkActionsBar from './components/BulkActionsBar';
import CreateInvoiceModal from './components/CreateInvoiceModal';
import InvoicePreviewModal from './components/InvoicePreviewModal';
import { useInvoices, useCreateInvoice, useUpdateInvoice, useDeleteInvoice, useApproveInvoice } from '../../hooks/useInvoices';
import { getCurrentCompanyId } from '../../utils/auth';
import { useAuth } from '../../context/AuthContext';
import { testApiConnection, testInvoicesAPI } from '../../utils/apiTest';

const Invoices = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [sortConfig, setSortConfig] = useState({ column: 'invoiceNumber', direction: 'desc' });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, invoice: null, isBulk: false });

  // Get company ID and use React Query hooks
  const companyId = getCurrentCompanyId();
  const { companyData } = useAuth();
  const { data: invoices = [], isLoading, error, refetch } = useInvoices(companyId);
  const createInvoiceMutation = useCreateInvoice();
  const updateInvoiceMutation = useUpdateInvoice();
  const deleteInvoiceMutation = useDeleteInvoice();
  const approveInvoiceMutation = useApproveInvoice();

  console.log('Invoices - Company ID:', companyId);
  console.log('Invoices - Invoices data:', invoices);
  console.log('Invoices - Loading:', isLoading);
  console.log('Invoices - Error:', error);

  const filteredAndSortedInvoices = useMemo(() => {
    let filtered = invoices?.filter(invoice => {
      const matchesSearch = 
        invoice?.invoiceNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        invoice?.customerName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        invoice?.customerEmail?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      const matchesStatus = statusFilter === 'all' || invoice?.status?.toLowerCase() === statusFilter?.toLowerCase();

      const matchesCustomer = customerFilter === 'all'|| invoice?.customerName?.toLowerCase()?.replace(/\s+/g,'-')?.includes(customerFilter);

      return matchesSearch && matchesStatus && matchesCustomer;
    });

    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.column];
      let bValue = b?.[sortConfig?.column];

      if (sortConfig?.column === 'amount' || sortConfig?.column === 'paidAmount') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else if (sortConfig?.column === 'issueDate' || sortConfig?.column === 'dueDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [invoices, searchTerm, statusFilter, customerFilter, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev?.column === column && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectInvoice = (id) => {
    setSelectedInvoices(prev =>
      prev?.includes(id) ? prev?.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedInvoices?.length === filteredAndSortedInvoices?.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(filteredAndSortedInvoices?.map(inv => inv?.id));
    }
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCustomerFilter('all');
    setDateRange('all');
  };

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
    setIsPreviewModalOpen(true);
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setIsEditModalOpen(true);
  };

  const handleDuplicate = (invoice) => {
    toast('Duplicate functionality coming soon', { icon: '📋' });
  };

  const handleSend = (invoice) => {
    toast('Send functionality coming soon', { icon: '📧' });
  };

  const handleApprove = async (invoice) => {
    try {
      await approveInvoiceMutation.mutateAsync(invoice.id);
      toast.success(`Invoice ${invoice?.invoiceNumber} approved`);
    } catch (error) {
      console.error('Failed to approve invoice:', error);
      toast.error(error?.message || 'Failed to approve invoice');
    }
  };

  const handleDelete = (invoice) => {
    setConfirmDialog({ isOpen: true, invoice, isBulk: false });
  };

  const handleConfirmDelete = async () => {
    const { invoice } = confirmDialog;
    setConfirmDialog({ isOpen: false, invoice: null, isBulk: false });
    try {
      await deleteInvoiceMutation.mutateAsync(invoice.id);
      toast.success(`Invoice ${invoice?.invoiceNumber} deleted`);
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error(error?.message || 'Failed to delete invoice');
    }
  };

  const handleBulkSend = () => {
    toast('Bulk send coming soon', { icon: '📧' });
    setSelectedInvoices([]);
  };

  const handleBulkMarkPaid = () => {
    toast('Bulk mark paid coming soon', { icon: '💳' });
    setSelectedInvoices([]);
  };

  const handleBulkDelete = () => {
    setConfirmDialog({ isOpen: true, invoice: null, isBulk: true });
  };

  const handleConfirmBulkDelete = async () => {
    const count = selectedInvoices.length;
    setConfirmDialog({ isOpen: false, invoice: null, isBulk: false });
    toast('Bulk delete coming soon', { icon: '🗑️' });
    setSelectedInvoices([]);
  };

  const handleCreateInvoice = async (invoiceData) => {
    try {
      console.log('Creating invoice with data:', invoiceData);
      await createInvoiceMutation.mutateAsync(invoiceData);
      setIsCreateModalOpen(false);
      toast.success('Invoice created successfully');
    } catch (error) {
      console.error('Failed to create invoice:', error);
      toast.error(error?.message || 'Failed to create invoice');
    }
  };

  const handleUpdateInvoice = async (invoiceData) => {
    try {
      console.log('Updating invoice with data:', invoiceData);
      await updateInvoiceMutation.mutateAsync({
        id: editingInvoice.id,
        data: invoiceData
      });
      setIsEditModalOpen(false);
      setEditingInvoice(null);
      toast.success('Invoice updated successfully');
    } catch (error) {
      console.error('Failed to update invoice:', error);
      toast.error(error?.message || 'Failed to update invoice');
    }
  };

  const handleTestAPI = async () => {
    console.log('🧪 Running API tests...');
    const result = await testApiConnection();
    console.log('API Test Result:', result);
    if (result.success) {
      toast.success('API test successful! Check console for details.');
    } else {
      toast.error(`API test failed: ${result.error}`);
    }
  };

  const handleTestInvoicesAPI = async () => {
    console.log('🧪 Running Invoices API test...');
    const result = await testInvoicesAPI(companyId);
    console.log('Invoices API Test Result:', result);
    if (result.success) {
      toast.success('Invoices API test successful! Check console for details.');
    } else {
      toast.error(`Invoices API test failed: ${result.error}`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <QuickActionToolbar />
      <main className="pt-16">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-2">
                Invoices
              </h1>
              <p className="font-body text-sm md:text-base text-muted-foreground">
                Manage and track all your invoices from creation to payment
              </p>
              {companyId && (
                <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                  <span>Company ID: {companyId}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleTestAPI}
                className="text-xs"
              >
                Test API
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleTestInvoicesAPI}
                className="text-xs"
              >
                Test Invoices
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => refetch()}
                iconName="RefreshCw"
                iconPosition="left"
                className="w-full sm:w-auto"
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Refresh'}
              </Button>
              <Button
                variant="default"
                size="lg"
                onClick={() => setIsCreateModalOpen(true)}
                iconName="Plus"
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                Create Invoice
              </Button>
            </div>
          </div>

          <InvoiceFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            dateRange={dateRange}
            setDateRange={setDateRange}
            customerFilter={customerFilter}
            setCustomerFilter={setCustomerFilter}
            onReset={handleResetFilters}
            totalResults={filteredAndSortedInvoices?.length}
          />

          {isLoading && (
            <div className="bg-card rounded-lg shadow-sm border border-border p-8 md:p-12 text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Icon name="Loader" size={32} className="text-muted-foreground animate-spin" />
              </div>
              <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-2">
                Loading invoices...
              </h3>
              <p className="font-body text-sm md:text-base text-muted-foreground">
                Please wait while we fetch your invoice data
              </p>
            </div>
          )}

          {error && !isLoading && (
            <div className="bg-card rounded-lg shadow-sm border border-destructive/20 p-8 md:p-12 text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertCircle" size={32} className="text-destructive" />
              </div>
              <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-2">
                Failed to load invoices
              </h3>
              <p className="font-body text-sm md:text-base text-muted-foreground mb-6">
                {error?.message || 'There was an error loading your invoices. Please try again.'}
              </p>
              <Button
                variant="outline"
                onClick={() => refetch()}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Try Again
              </Button>
            </div>
          )}

          {!isLoading && !error && (
            <>
              <div className="hidden lg:block">
                <InvoiceTable
                  invoices={filteredAndSortedInvoices}
                  selectedInvoices={selectedInvoices}
                  onSelectInvoice={handleSelectInvoice}
                  onSelectAll={handleSelectAll}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDuplicate={handleDuplicate}
                  onSend={handleSend}
                  onApprove={handleApprove}
                  onDelete={handleDelete}
                />
              </div>

              <div className="lg:hidden space-y-3">
                {filteredAndSortedInvoices?.map(invoice => (
                  <InvoiceMobileCard
                    key={invoice?.id}
                    invoice={invoice}
                    isSelected={selectedInvoices?.includes(invoice?.id)}
                    onSelect={handleSelectInvoice}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDuplicate={handleDuplicate}
                    onSend={handleSend}
                    onApprove={handleApprove}
                    onDelete={handleDelete}
                  />
                ))}
              </div>

              {filteredAndSortedInvoices?.length === 0 && (
                <div className="bg-card rounded-lg shadow-sm border border-border p-8 md:p-12 text-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="FileText" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-2">
                    No invoices found
                  </h3>
                  <p className="font-body text-sm md:text-base text-muted-foreground mb-6">
                    {searchTerm || statusFilter !== 'all' || customerFilter !== 'all' || dateRange !== 'all'
                      ? 'Try adjusting your filters or create a new invoice'
                      : 'Create your first invoice to get started'
                    }
                  </p>
                  <Button
                    variant="default"
                    onClick={() => setIsCreateModalOpen(true)}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Create Your First Invoice
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <BulkActionsBar
        selectedCount={selectedInvoices?.length}
        onSendBulk={handleBulkSend}
        onMarkPaid={handleBulkMarkPaid}
        onDelete={handleBulkDelete}
        onClearSelection={() => setSelectedInvoices([])}
      />
      <CreateInvoiceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateInvoice}
      />
      <CreateInvoiceModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingInvoice(null);
        }}
        onSubmit={handleUpdateInvoice}
        invoice={editingInvoice}
        isEditMode={true}
      />
      <InvoicePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => {
          setIsPreviewModalOpen(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.isBulk ? `Delete ${selectedInvoices.length} invoices?` : `Delete ${confirmDialog.invoice?.invoiceNumber}?`}
        message={confirmDialog.isBulk
          ? `This will permanently delete ${selectedInvoices.length} selected invoices. This action cannot be undone.`
          : 'This invoice will be permanently deleted. This action cannot be undone.'
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirmDialog.isBulk ? handleConfirmBulkDelete : handleConfirmDelete}
        onCancel={() => setConfirmDialog({ isOpen: false, invoice: null, isBulk: false })}
      />
    </div>
  );
};

export default Invoices;